import { ChangeEvent, useCallback, useState } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  ManageMembershipRequestType,
  SupportEventStatus,
  useCreateEnrollmentSupportEventMutation,
  useUpdateEnrollmentSupportEventMutation,
} from 'shared/types/graphql';

interface CareNoteInput {
  enrollmentId: string;
}

interface CareNoteOutput {
  isNoteEditorOpen: boolean;
  isSaving: boolean;
  note: string;
  handleEdit: () => void;
  handleSave: () => void;
  handleCancel: () => void;
  handleChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  handleDelete: (id: string) => void;
}

export const useCareNote = ({ enrollmentId }: CareNoteInput): CareNoteOutput => {
  const [createEnrollmentSupportEvent] = useCreateEnrollmentSupportEventMutation();
  const [updateEnrollmentSupportEvent] = useUpdateEnrollmentSupportEventMutation();
  const { clinicUser } = useClinicUser();
  const [isNoteEditorOpen, setIsNoteEditorOpen] = useState(false);
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    await createEnrollmentSupportEvent({
      variables: {
        data: {
          requester: { connect: { id: clinicUser?.id } },
          manageMembershipRequestType: ManageMembershipRequestType.Note,
          carePlanEnrollment: { connect: { id: enrollmentId } },
          status: SupportEventStatus.Completed,
          manageMembershipDescription: note,
        },
      },
    });
    setNote('');
    setIsSaving(false);
    setIsNoteEditorOpen(false);
  }, [clinicUser?.id, createEnrollmentSupportEvent, enrollmentId, note]);

  const handleDelete = useCallback(
    async (id: string) => {
      await updateEnrollmentSupportEvent({
        variables: {
          where: {
            id,
          },
          data: {
            status: SupportEventStatus.Canceled,
          },
        },
      });
    },
    [updateEnrollmentSupportEvent],
  );

  const handleCancel = useCallback(() => {
    setIsNoteEditorOpen(false);
    setNote('');
  }, []);

  const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  }, []);

  const handleEdit = useCallback(() => {
    setIsNoteEditorOpen(true);
  }, []);

  return {
    isNoteEditorOpen,
    isSaving,
    note,
    handleEdit,
    handleSave,
    handleCancel,
    handleChange,
    handleDelete,
  };
};
